'use client';

import React, { useState, useRef } from 'react';
import {
  Scan, Package, Hash, Calendar, User, Clock,
  AlertTriangle, CheckCircle2, ChevronRight, RotateCcw,
  Layers, Thermometer, ShieldCheck, Info, Search
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChildItem {
  id: string;
  itemName: string;
  batchNumber: string;
  quantity: string;
  uom: string;
  mfgDate: string;
  expiryDate: string;
  shelfLifePct: number | null;
  rackLocation: string;
  grnId: string;
  status: 'ok' | 'expiring' | 'expired';
}

interface KitRecord {
  kitBarcode: string;
  kitId: string;
  kitName: string;
  completedAt: string;
  completedDate: string;
  operator: string;
  operatorId: string;
  device: string;
  totalItems: number;
  childItems: ChildItem[];
}

// ─── Mock Kit Data ─────────────────────────────────────────────────────────────

const KIT_DATABASE: Record<string, KitRecord> = {
  'KIT-20260612-001': {
    kitBarcode: 'KIT-20260612-001',
    kitId: 'KIT-20260612-001',
    kitName: 'Surgical Dressing Kit — Type A',
    completedAt: '09:42 AM',
    completedDate: '12 Jun 2026',
    operator: 'Prasun Sharma',
    operatorId: 'OPR-001',
    device: 'HH-01',
    totalItems: 5,
    childItems: [
      {
        id: 'CI-001',
        itemName: 'Non Woven Tape 75mm x 250cm',
        batchNumber: 'BAT-2026-001',
        quantity: '2',
        uom: 'ROLL',
        mfgDate: '01 Jan 2026',
        expiryDate: '01 Jun 2027',
        shelfLifePct: 82,
        rackLocation: 'A-01-02-B-05',
        grnId: 'GRN-20260612-001',
        status: 'ok',
      },
      {
        id: 'CI-002',
        itemName: 'Gauze Swab 10x10cm Sterile',
        batchNumber: 'BAT-2026-007',
        quantity: '5',
        uom: 'PCS',
        mfgDate: '15 Feb 2026',
        expiryDate: '30 Jun 2026',
        shelfLifePct: 42,
        rackLocation: 'A-01-02-B-03',
        grnId: 'GRN-20260611-087',
        status: 'expiring',
      },
      {
        id: 'CI-003',
        itemName: 'Surgical Gloves Size M',
        batchNumber: 'BAT-2026-006',
        quantity: '2',
        uom: 'PAIR',
        mfgDate: '10 Mar 2026',
        expiryDate: '15 Mar 2027',
        shelfLifePct: 75,
        rackLocation: 'A-01-01-A-03',
        grnId: 'GRN-20260612-002',
        status: 'ok',
      },
      {
        id: 'CI-004',
        itemName: 'BP Scalpel Handle Size 4',
        batchNumber: 'AUTO-345604',
        quantity: '1',
        uom: 'PCS',
        mfgDate: '05 Apr 2026',
        expiryDate: 'No Expiry',
        shelfLifePct: null,
        rackLocation: 'C-03-01-B-02',
        grnId: 'GRN-20260612-002',
        status: 'ok',
      },
      {
        id: 'CI-005',
        itemName: 'Mayo S Trolley Cover Small',
        batchNumber: 'BAT-2026-003',
        quantity: '1',
        uom: 'PCS',
        mfgDate: '20 Jan 2026',
        expiryDate: '01 Sep 2028',
        shelfLifePct: 91,
        rackLocation: 'B-02-03-A-01',
        grnId: 'GRN-20260612-001',
        status: 'ok',
      },
    ],
  },
  'KIT-20260611-045': {
    kitBarcode: 'KIT-20260611-045',
    kitId: 'KIT-20260611-045',
    kitName: 'Wound Care Kit — Standard',
    completedAt: '02:15 PM',
    completedDate: '11 Jun 2026',
    operator: 'Anita Desai',
    operatorId: 'OPR-003',
    device: 'HH-02',
    totalItems: 3,
    childItems: [
      {
        id: 'CI-101',
        itemName: 'Adhesive Bandage 25mm x 72mm',
        batchNumber: 'BAT-2026-011',
        quantity: '10',
        uom: 'PCS',
        mfgDate: '01 Mar 2026',
        expiryDate: '01 Mar 2028',
        shelfLifePct: 88,
        rackLocation: 'A-02-01-A-01',
        grnId: 'GRN-20260610-033',
        status: 'ok',
      },
      {
        id: 'CI-102',
        itemName: 'Antiseptic Wipes 70% IPA',
        batchNumber: 'BAT-2026-009',
        quantity: '3',
        uom: 'PCS',
        mfgDate: '10 Feb 2026',
        expiryDate: '10 Feb 2027',
        shelfLifePct: 68,
        rackLocation: 'B-01-02-A-02',
        grnId: 'GRN-20260610-033',
        status: 'ok',
      },
      {
        id: 'CI-103',
        itemName: 'Sterile Saline 0.9% 100ml',
        batchNumber: 'BAT-2026-014',
        quantity: '1',
        uom: 'BTL',
        mfgDate: '05 Jan 2026',
        expiryDate: '05 Jan 2027',
        shelfLifePct: 55,
        rackLocation: 'D-01-01-A-01',
        grnId: 'GRN-20260609-021',
        status: 'ok',
      },
    ],
  },
};

// ─── Shelf life bar ───────────────────────────────────────────────────────────

function ShelfLifeBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-2xs text-muted-foreground italic">No Expiry</span>;
  const color = pct >= 80 ? 'bg-primary' : pct >= 50 ? 'bg-accent' : 'bg-red-400';
  const textColor = pct >= 80 ? 'text-primary' : pct >= 50 ? 'text-accent' : 'text-red-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[48px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-2xs font-bold tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function ItemStatusBadge({ status }: { status: ChildItem['status'] }) {
  if (status === 'expiring') {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold bg-accent/15 text-accent border border-accent/30">
        <AlertTriangle size={9} />
        Expiring Soon
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold bg-red-500/15 text-red-400 border border-red-500/30">
        <AlertTriangle size={9} />
        Expired
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-bold bg-primary/10 text-primary border border-primary/20">
      <CheckCircle2 size={9} />
      OK
    </span>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────

export default function KitTraceabilityBoard() {
  const [scanInput, setScanInput] = useState('');
  const [kitRecord, setKitRecord] = useState<KitRecord | null>(null);
  const [scanState, setScanState] = useState<'idle' | 'found' | 'not_found'>('idle');
  const [flashClass, setFlashClass] = useState('');
  const scanRef = useRef<HTMLInputElement>(null);

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    const val = scanInput.trim();
    if (!val) return;

    const found = KIT_DATABASE[val] ?? null;

    if (found) {
      setKitRecord(found);
      setScanState('found');
      setFlashClass('ring-2 ring-primary/60');
      setTimeout(() => setFlashClass(''), 1000);
    } else {
      setKitRecord(null);
      setScanState('not_found');
      setFlashClass('ring-2 ring-red-400/60');
      setTimeout(() => {
        setFlashClass('');
        setScanState('idle');
      }, 1500);
    }
    setScanInput('');
  };

  const handleReset = () => {
    setKitRecord(null);
    setScanState('idle');
    setScanInput('');
    setFlashClass('');
    setTimeout(() => scanRef.current?.focus(), 50);
  };

  const hasAlert = kitRecord?.childItems.some((i) => i.status === 'expiring' || i.status === 'expired');

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-hidden">
      {/* Scan bar */}
      <form onSubmit={handleScan} className="flex gap-3">
        <div className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border transition-all duration-300 ${flashClass}`}>
          <Scan size={18} className="text-primary flex-shrink-0" />
          <input
            ref={scanRef}
            type="text"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            placeholder="Scan completed kit barcode (e.g. KIT-20260612-001)"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            autoFocus
          />
          {scanInput && (
            <button type="submit" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors">
              <Search size={12} />
              Lookup
            </button>
          )}
        </div>
        {kitRecord && (
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <RotateCcw size={14} />
            Clear
          </button>
        )}
      </form>

      {/* Not found state */}
      {scanState === 'not_found' && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 font-medium">Kit barcode not found. Please verify and scan again.</p>
        </div>
      )}

      {/* Idle / empty state */}
      {scanState === 'idle' && !kitRecord && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Scan size={28} className="text-primary" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground">Scan a Kit Barcode</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Scan or enter a completed kit barcode to view all child items, batch numbers, mfg/expiry dates, operator, and timestamp for recall verification.
            </p>
          </div>
          <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
            <p className="font-medium text-foreground/60">Try demo barcodes:</p>
            {Object.keys(KIT_DATABASE).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => { setScanInput(k); setTimeout(() => scanRef.current?.focus(), 50); }}
                className="px-3 py-1 rounded-lg bg-muted hover:bg-muted/80 text-xs font-mono text-foreground transition-colors"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kit record found */}
      {kitRecord && (
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Recall alert banner */}
          {hasAlert && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-accent/10 border border-accent/30">
              <AlertTriangle size={16} className="text-accent flex-shrink-0" />
              <p className="text-sm text-accent font-medium">
                ⚠ This kit contains items expiring soon — review before dispatch.
              </p>
            </div>
          )}

          {/* Kit summary card */}
          <div className="rounded-xl bg-card border border-border p-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Layers size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">{kitRecord.kitName}</h2>
                  <p className="text-xs font-mono text-muted-foreground mt-0.5">{kitRecord.kitId}</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                <CheckCircle2 size={12} />
                Completed
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <User size={11} />
                  Operator
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.operator}</p>
                <p className="text-2xs text-muted-foreground">{kitRecord.operatorId} · {kitRecord.device}</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <Clock size={11} />
                  Completed At
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.completedAt}</p>
                <p className="text-2xs text-muted-foreground">{kitRecord.completedDate}</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <Package size={11} />
                  Total Items
                </div>
                <p className="text-xs font-semibold text-foreground">{kitRecord.totalItems} components</p>
                <p className="text-2xs text-muted-foreground">All scanned &amp; verified</p>
              </div>
              <div className="flex flex-col gap-1 px-3 py-2.5 rounded-lg bg-muted/40">
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <ShieldCheck size={11} />
                  Recall Status
                </div>
                <p className={`text-xs font-semibold ${hasAlert ? 'text-accent' : 'text-primary'}`}>
                  {hasAlert ? 'Review Required' : 'Clear'}
                </p>
                <p className="text-2xs text-muted-foreground">{hasAlert ? 'Items expiring soon' : 'No issues found'}</p>
              </div>
            </div>
          </div>

          {/* Child items table */}
          <div className="rounded-xl bg-card border border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Layers size={15} className="text-primary" />
                <span className="text-sm font-semibold text-foreground">Child Items</span>
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-2xs font-bold">{kitRecord.childItems.length}</span>
              </div>
              <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                <Info size={11} />
                All batch &amp; date data for recall verification
              </div>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/30 border-b border-border text-2xs font-semibold text-muted-foreground uppercase tracking-wide">
              <div className="col-span-3">Item Name</div>
              <div className="col-span-2">Batch No.</div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2">Mfg Date</div>
              <div className="col-span-2">Expiry Date</div>
              <div className="col-span-1">Shelf Life</div>
              <div className="col-span-1 text-right">Status</div>
            </div>

            {/* Table rows */}
            <div className="divide-y divide-border">
              {kitRecord.childItems.map((item) => (
                <div
                  key={item.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-muted/20 transition-colors ${
                    item.status === 'expiring' ? 'bg-accent/5' : item.status === 'expired' ? 'bg-red-500/5' : ''
                  }`}
                >
                  {/* Item name */}
                  <div className="col-span-3">
                    <p className="text-xs font-semibold text-foreground leading-tight">{item.itemName}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-2xs text-muted-foreground font-mono">{item.grnId}</span>
                      <ChevronRight size={9} className="text-muted-foreground" />
                      <span className="text-2xs text-muted-foreground">{item.rackLocation}</span>
                    </div>
                  </div>

                  {/* Batch */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Hash size={10} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs font-mono text-foreground truncate">{item.batchNumber}</span>
                    </div>
                    <p className="text-2xs text-muted-foreground mt-0.5">{item.uom}</p>
                  </div>

                  {/* Qty */}
                  <div className="col-span-1 text-center">
                    <span className="text-xs font-bold text-foreground">{item.quantity}</span>
                  </div>

                  {/* Mfg date */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={10} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-xs text-foreground">{item.mfgDate}</span>
                    </div>
                  </div>

                  {/* Expiry date */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Thermometer size={10} className={`flex-shrink-0 ${item.status === 'expiring' ? 'text-accent' : item.status === 'expired' ? 'text-red-400' : 'text-muted-foreground'}`} />
                      <span className={`text-xs font-medium ${item.status === 'expiring' ? 'text-accent' : item.status === 'expired' ? 'text-red-400' : 'text-foreground'}`}>
                        {item.expiryDate}
                      </span>
                    </div>
                  </div>

                  {/* Shelf life */}
                  <div className="col-span-1">
                    <ShelfLifeBar pct={item.shelfLifePct} />
                  </div>

                  {/* Status */}
                  <div className="col-span-1 flex justify-end">
                    <ItemStatusBadge status={item.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
